import React, { useState } from 'react';
import { Button } from '../../../components/ui/button';
import { FileSpreadsheet, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../../utils/axios';
import { fmtMoney, fmtNum } from './kpiUtils';

const fmtCelda = (v, tipo) => {
  if (v === null || v === undefined || v === '') return '—';
  if (tipo === 'moneda') return fmtMoney(v);
  if (tipo === 'numero') return fmtNum(v);
  if (tipo === 'pct') return `${Number(v).toLocaleString('es-AR', { maximumFractionDigits: 1 })}%`;
  return v;
};

const conTotal = (columnas, filas) => {
  const total = { [columnas[0].key]: 'Total', _total: true };
  columnas.slice(1).forEach((c) => {
    total[c.key] = ['numero', 'moneda'].includes(c.tipo) ? filas.reduce((s, f) => s + (Number(f[c.key]) || 0), 0) : null;
  });
  return [...filas, total];
};

// Tabla con los datos de un gráfico + exportación a Excel.
// columnas: [{ key, label, tipo: 'texto'|'numero'|'moneda'|'pct' }] (la primera es la etiqueta de fila)
export const ChartDataTable = ({ titulo, columnas, filas, total = false, testId = 'chart-data-table' }) => {
  const [exportando, setExportando] = useState(false);
  const rows = total && filas.length ? conTotal(columnas, filas) : filas;

  const exportar = async () => {
    setExportando(true);
    try {
      const r = await api.post('/kpis/exportar-tabla', { titulo, columnas, filas: rows }, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${titulo.replace(/[^\w\- ]+/g, '').trim() || 'tabla'}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('No se pudo exportar a Excel');
    } finally { setExportando(false); }
  };

  return (
    <div className="mt-5 border-t border-slate-100 dark:border-slate-800 pt-4" data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Datos del gráfico</p>
        <Button size="sm" variant="outline" onClick={exportar} disabled={exportando || !filas.length} className="h-8 text-xs" data-testid={`${testId}-excel`}>
          {exportando ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <FileSpreadsheet size={13} className="mr-1.5 text-emerald-600" />}
          Exportar a Excel
        </Button>
      </div>
      <div className="overflow-x-auto max-h-80 overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-700">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 dark:bg-slate-800 text-slate-500 uppercase text-[10px] sticky top-0">
            <tr>
              {columnas.map((c, i) => (
                <th key={c.key} className={`px-3 py-2 font-semibold whitespace-nowrap ${i === 0 ? 'text-left' : 'text-right'}`}>{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {rows.map((f, ri) => (
              <tr key={ri} className={f._total ? 'sticky bottom-0 bg-slate-100 dark:bg-slate-800 font-bold' : 'hover:bg-slate-50/60'} data-testid={`${testId}-row-${ri}`}>
                {columnas.map((c, i) => (
                  <td key={c.key} className={`px-3 py-1.5 tabular-nums whitespace-nowrap ${i === 0 ? 'text-left text-slate-700 dark:text-slate-200' : 'text-right text-slate-700 dark:text-slate-300'}`}>
                    {fmtCelda(f[c.key], c.tipo)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ChartDataTable;
